import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { VscSettings } from "react-icons/vsc"
import { useSearchParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import Bottom from "@fuse/sharedComponents/bottom"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import BlurredLoadingSpinner from "@fuse/sharedComponents/loader/BlurredLoadingSpinner"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import { interestsData } from "@letshang/constants"
import { debounce, isEmpty } from "lodash-es"

import { useSearchedEvent } from "@api/services/events/useEventsApi"

import { isDev } from "@fuse/utils/DeviceDection"

import EmptySearchState from "@fuse/pages/explore/component/EmptySearchState"
import SideBar from "@fuse/pages/explore/component/SideBar"
import Tag from "@fuse/pages/explore/component/Tag"
import EventCard from "@fuse/pages/landingPage/components/EventCard"

const bgImage = "/assets/alpha-event-not-found.svg"

const PAGE_SIZE = 10

export const DateRangeWithName = {
  THIS_WEEK: {
    title: "This Week",
    value: "thisWeek",
  },
  THIS_MONTH: {
    title: "This Month",
    value: "thisMonth",
  },
  NEXT_WEEK: {
    title: "Next Week",
    value: "nextWeek",
  },
} as const

export type TDateRangeWithName =
  (typeof DateRangeWithName)[keyof typeof DateRangeWithName]["value"]

export type TExploreSearchArgs = {
  searchTerm: string
  tags: string[]
  location?: {
    lat: number
    lon: number
    nameOfPlace: string
  }
  dateRangeType: TDateRangeWithName
}

export type SearchPramsType = {
  location: {
    lat: number
    lon: number
  }
  searchTerm: string
  tags: string[]
  dateRangeType: TDateRangeWithName
}

export type SearchParamsTypeDef = Partial<SearchPramsType>

export type ExtentedSearchParamsTypeDefWithOffset = SearchParamsTypeDef & {
  offset: number
  pageSize: number
}

const Explore = () => {
  const [isVisible, setIsVisible] = useState(false)
  const options = useMemo(
    () =>
      interestsData.reduce((acc, interest) => {
        acc[interest.toLowerCase()] = false
        return acc
      }, {}),
    [],
  )
  const tagsRef = useRef<HTMLDivElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchArgs, setSearchArgs] = useState<TExploreSearchArgs>({
    location: {
      lon: 0,
      lat: 0,
      nameOfPlace: "",
    },
    searchTerm: "",
    tags: [],
    dateRangeType: DateRangeWithName.THIS_WEEK.value,
  })
  const [offset, setOffset] = useState<{
    offsetForLocationBasedQuery: number
    offsetForSearchAndTagbasedQuery: number
  }>({
    offsetForLocationBasedQuery: 0,
    offsetForSearchAndTagbasedQuery: 0,
  })
  const [whatsToFocus, setWhatToFocus] = useState("")
  const [interestsDataObj, setInterestsDataObj] =
    useState<Record<string, boolean>>(options)
  const [debouncedSearchArgs, setDebouncedSearchArgs] = useState(searchArgs)

  const setTheQueryFromUrl = (
    type: string,
    value: SearchPramsType[keyof SearchPramsType],
  ) => {
    switch (type) {
      case "tags": {
        const tagsVal: SearchPramsType["tags"] =
          value as SearchPramsType["tags"]

        // converting the obtained value from the url to {option: boolean}
        const selected = tagsVal.reduce((acc, interest) => {
          if (
            interestsData.find((i) => i.toLowerCase() == interest.toLowerCase())
          )
            acc[interest.toLowerCase()] = true //filtering the tags which exist in our system
          return acc
        }, {})

        setInterestsDataObj((prev) => ({
          ...prev,
          ...selected,
        }))
        break
      }
      case "searchTerm": {
        const searchTermVal: SearchPramsType["searchTerm"] =
          value as SearchPramsType["searchTerm"]
        setSearchArgs((prev) => ({
          ...prev,
          searchTerm: searchTermVal,
        }))
        break
      }
      case "location": {
        const locationVal: SearchPramsType["location"] =
          value as SearchPramsType["location"]

        setSearchArgs((prev) => ({
          ...prev,
          location: {
            ...prev.location,
            ...locationVal,
          },
        }))
        break
      }

      case "dateRangeType": {
        const dateRangeTypeVal: SearchPramsType["dateRangeType"] =
          value as SearchPramsType["dateRangeType"]
        setSearchArgs((prev) => ({
          ...prev,
          dateRangeType: dateRangeTypeVal,
        }))
        break
      }
    }
  }

  // Debounce function
  const debouncedUpdate = useMemo(
    () =>
      debounce((newSearchArgs) => {
        setDebouncedSearchArgs(newSearchArgs)
        setQueryInUrl(newSearchArgs)
      }, 300),
    [],
  )
  const getTheParams = (isLocationBased = false): SearchParamsTypeDef => {
    const queryBody: Partial<ExtentedSearchParamsTypeDefWithOffset> = {}

    if (searchArgs?.location?.lat && searchArgs?.location?.lon) {
      queryBody.location = {
        lat: searchArgs?.location?.lat,
        lon: searchArgs?.location?.lon,
      }
    }

    if (searchArgs?.dateRangeType) {
      queryBody.dateRangeType = searchArgs?.dateRangeType
    }

    if (isLocationBased) {
      return queryBody
    }

    if (searchArgs?.searchTerm) {
      queryBody.searchTerm = searchArgs?.searchTerm
    }

    // queryBody.offset = isLocationBased
    //   ? offset.offsetForLocationBasedQuery
    //   : offset.offsetForSearchAndTagbasedQuery
    // queryBody.pageSize = PAGE_SIZE

    if (searchArgs?.tags?.length) {
      queryBody.tags = searchArgs?.tags
    }

    return queryBody
  }

  const queryBodyForLocationBased = useMemo(() => {
    return getTheParams(true)
  }, [
    debouncedSearchArgs?.location,
    debouncedSearchArgs?.dateRangeType,
    offset,
  ])

  const queryBodyForAllFilter = useMemo(() => {
    return getTheParams()
  }, [debouncedSearchArgs, offset])

  const {
    searchedResult: searchedResultForAllFilter,
    isLoading: isLoadingForAllFilter,
    totalHits: totalHitForAgainstAllFilter,
    refetchByTerm: refetchByTermAndTag,
  } = useSearchedEvent(queryBodyForAllFilter, true, "allFilter")

  let isLocationBasedSearchEnabled = useMemo(
    () => !searchedResultForAllFilter?.length && !isLoadingForAllFilter,
    [searchedResultForAllFilter, isLoadingForAllFilter],
  )

  const {
    searchedResult: searchedResultForLocationBased,
    isLoading: isLoadingForLocation,
    totalHits: totalLocationHit,
    refetchByTerm: refetchByLocation,
  } = useSearchedEvent(
    queryBodyForLocationBased,
    isLocationBasedSearchEnabled,
    "locationBased",
  )

  isLocationBasedSearchEnabled = false

  const shouldRenderComponent =
    (searchedResultForLocationBased?.length ||
      searchedResultForAllFilter?.length) &&
    !isLoadingForLocation &&
    !isLoadingForAllFilter

  const shouldRenderEmptyState =
    !searchedResultForLocationBased?.length &&
    !searchedResultForAllFilter?.length &&
    !isLoadingForLocation &&
    !isLoadingForAllFilter

  const handleChangeLocationBtnClick = (compName: string) => {
    setIsVisible(true)
    setWhatToFocus(compName)
  }

  const updateOffset = (isLocationBased = false) => {
    setOffset((prev) => {
      if (isLocationBased) {
        return {
          ...prev,
          offsetForLocationBasedQuery:
            prev.offsetForLocationBasedQuery + PAGE_SIZE,
        }
      } else {
        return {
          ...prev,
          offsetForSearchAndTagbasedQuery:
            prev.offsetForSearchAndTagbasedQuery + PAGE_SIZE,
        }
      }
    })
  }

  const setQueryInUrl = (data: TExploreSearchArgs) => {
    const queryObj = Object.entries(data).reduce((acc, [key, val]) => {
      const keyToProcess = key as keyof TExploreSearchArgs
      if (!isEmpty(val)) {
        switch (keyToProcess) {
          case "searchTerm": {
            const valueToProcess = val as TExploreSearchArgs["searchTerm"]
            acc["searchTerm"] = valueToProcess
            break
          }
          case "location": {
            const valueToProcess = val as TExploreSearchArgs["location"]
            if (valueToProcess?.lat || valueToProcess?.lon) {
              acc["location"] = `${valueToProcess.lat},${valueToProcess.lon}`
            }
            break
          }
          case "tags": {
            const valueToProcess = val as TExploreSearchArgs["tags"]
            acc["tags"] = arrayToString(valueToProcess)
            break
          }
          case "dateRangeType": {
            const valueToProcess = val as TExploreSearchArgs["dateRangeType"]
            acc["dateRangeType"] = valueToProcess
            break
          }
        }

        return acc
      } else {
        return acc
      }
    }, {})
    setSearchParams(queryObj)
  }

  const getTheTimePeriodVal = (type: TDateRangeWithName) => {
    switch (type) {
      case DateRangeWithName.NEXT_WEEK.value:
        return DateRangeWithName.NEXT_WEEK.title

      case DateRangeWithName.THIS_WEEK.value:
        return DateRangeWithName.THIS_WEEK.title

      case DateRangeWithName.THIS_MONTH.value:
        return DateRangeWithName.THIS_MONTH.title
    }
  }

  function arrayToString<T>(arr: Array<T>): string {
    if (typeof arr === "object" && Array.isArray(arr))
      return arr.reduce((acc, currentIndex, index) => {
        if (currentIndex) {
          const sanitizedval = String(currentIndex).trim()
          console.log("sanitizedval:", sanitizedval)
          if (index == 0 && sanitizedval) {
            return acc + String(sanitizedval)
          } else if (sanitizedval) return acc + "," + String(sanitizedval)
        }
        return acc
      }, "")
    else {
      if (isDev()) {
        throw Error("TypeError: must be an array")
      } else {
        console.error("TypeError: must be an array")
      }
    }
  }

  const handleLocationClick = () => {
    handleChangeLocationBtnClick("locationComp")
  }

  const handleTimeDurationClick = () => {
    handleChangeLocationBtnClick("dateRangeComp")
  }

  const handleScroll = useCallback(() => {
    let poisitionFromTop = 0

    if (tagsRef.current)
      poisitionFromTop = tagsRef.current.getBoundingClientRect().top

    if (!poisitionFromTop) {
      tagsRef.current.classList.add("backdrop-blur-md")
    } else {
      tagsRef.current.classList.remove("backdrop-blur-md")
    }
  }, [])

  useEffect(() => {
    for (const key of searchParams.keys()) {
      switch (key) {
        case "tags": {
          const tagStr = searchParams.get(key)
          if (tagStr) setTheQueryFromUrl(key, tagStr.split(","))
          break
        }
        case "searchTerm": {
          setTheQueryFromUrl(key, searchParams.get(key))
          break
        }
        case "location": {
          const [lat, lon] = searchParams
            .get(key)
            .split(",")
            .map((i) => Number(i))
          setTheQueryFromUrl(key, {
            lon,
            lat,
          })
          break
        }
        case "dateRangeType": {
          setTheQueryFromUrl(key, searchParams.get(key))
          break
        }
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const tagArr: string[] = []
    Object.keys(interestsDataObj).forEach((item: string) => {
      if (interestsDataObj[item]) {
        tagArr.push(item.toString())
      }
    })

    setSearchArgs((prev) => {
      return {
        ...prev,
        tags: tagArr,
      }
    })
  }, [interestsDataObj])

  useEffect(() => {
    if (searchArgs.searchTerm === "") {
      setDebouncedSearchArgs(searchArgs) // Update state immediately
    }

    debouncedUpdate(searchArgs)
    return () => debouncedUpdate.cancel()
  }, [searchArgs])

  useEffect(() => {
    if (!isVisible) {
      setWhatToFocus("")
    }
  }, [isVisible])

  return (
    <BackgroundWrapper bgImage={bgImage} className="h-full  !max-w-screen ">
      <BlurWrapper
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-screen  flex flex-col flex-1 "
      >
        <div id="upper-body" className="flex">
          <Navbar />
        </div>

        {(isLoadingForAllFilter || isLoadingForLocation) && (
          <BlurredLoadingSpinner />
        )}

        {/* applied filters  */}
        <div
          // ref={sidebarRef}
          className="px-4 md:px-8 py-4 lg:px-12 xl:px-16 2xl:px-24 "
        >
          <div className="  ">
            {/* place and time */}
            <div className="flex gap-3 ">
              <div className="text-[white] text-md sm:text-lg md:text-xl lg:text-2xl font-semibold text-nowrap ">
                Events in
              </div>
              <div
                onClick={handleLocationClick}
                className="hover:cursor-pointer text-md sm:text-lg md:text-xl lg:text-2xl font-semibold text-[#FFFFFF80] underline text-ellipsis text-nowrap overflow-clip max-w-[15ch] md:max-w-[20ch] lg:max-w-[40ch] w-fit"
              >
                {searchArgs?.location?.nameOfPlace
                  ? searchArgs?.location?.nameOfPlace
                  : "Select Location"}
              </div>
              <div
                onClick={handleTimeDurationClick}
                className="hover:cursor-pointer text-md sm:text-lg md:text-xl lg:text-2xl font-semibold text-[#FFFFFF80] underline text-ellipsis text-nowrap overflow-clip min-w-max"
              >
                {getTheTimePeriodVal(searchArgs?.dateRangeType)}
              </div>
            </div>
          </div>
        </div>
        {/* tags */}

        <div
          ref={tagsRef}
          style={{
            zIndex: 100,
            position: "sticky",
            top: 0,
          }}
          className=" px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-3 md:py-6  flex overflow-auto"
        >
          {/* make it selected when there is some place in any of the filter */}
          <Tag
            isSelected={
              !!(
                searchArgs?.tags.length ||
                (searchArgs?.location?.lat && searchArgs?.location?.lon) ||
                searchArgs?.dateRangeType
              )
            }
            onClick={() => setIsVisible(!isVisible)}
          >
            <VscSettings name={"vsc"} color="white" size={16} />
          </Tag>
          {interestsData.slice(0, 8).map((item, _) => {
            return (
              <Tag
                isSelected={interestsDataObj[item.toLowerCase()]}
                tagTitle={item}
                key={_}
                onClick={() =>
                  setInterestsDataObj((prev) => ({
                    ...prev,
                    [item.toLowerCase()]: !prev[item.toLowerCase()],
                  }))
                }
              />
            )
          })}

          <Tag
            tagTitle={"View All"}
            isSelected={false}
            customNonSelectedStyle={"!bg-[#7F7F7F1A]/20 !text-[white] "}
            onClick={() => setIsVisible(!isVisible)}
          />
        </div>

        <>
          <div className="flex-grow ">
            {shouldRenderEmptyState && (
              <div className=" px-4 md:px-8 py-4 lg:px-12 xl:px-16 2xl:px-24 px-auto flex-1 place-content-center">
                <EmptySearchState
                  onClickOfChangeLocation={handleLocationClick}
                />
              </div>
            )}

            {/* result */}

            {shouldRenderComponent ? (
              <div className="px-4 md:px-8 py-4 lg:px-12 xl:px-16 2xl:px-24 px-auto ">
                {/* best search */}
                <div>
                  <div className="text-md sm:text-lg  md:text-xl lg:text-2xl font-semibold text-white text-left flex pb-2">
                    <div>
                      {searchedResultForAllFilter?.length
                        ? "Best matches"
                        : "No exact matches"}
                    </div>
                    <div className="text-[#C2C2C2]">
                      ({searchedResultForAllFilter?.length})
                    </div>
                  </div>

                  {searchedResultForAllFilter?.length ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full my-4">
                      {searchedResultForAllFilter?.map((item, index) => {
                        const eventId = item?._id || item?._source?.id
                        return (
                          <div className="min-w-[300px] " key={eventId}>
                            <EventCard
                              key={index.toString()}
                              event={{ ...item?._source, eventId }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="flex justify-start items-start flex-col  gap-4">
                      <div className="text-[#C2C2C2] text-sm md:text-lg  font-medium text-start">
                        Try changing or removing some filters to discover more
                        options
                      </div>
                      <div>
                        <Button
                          variant="solid"
                          className="rounded-lg"
                          onClick={() => {
                            setIsVisible(true)
                          }}
                        >
                          <div className="px-8">Update Filter</div>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* nearest search */}
                {!searchedResultForAllFilter?.length && (
                  <div className="py-4">
                    <div className="text-md sm:text-lg md:text-xl lg:text-2xl font-semibold text-white text-left  flex">
                      <div>Nearest Matches</div>
                      <div className="text-[#C2C2C2]">
                        ({searchedResultForLocationBased?.length})
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full my-8">
                      {searchedResultForLocationBased?.map((item, index) => {
                        const eventId = item?._id || item?._source?.id
                        return (
                          <div className="min-w-[300px] ">
                            <EventCard
                              key={index.toString()}
                              event={{ ...item?._source, eventId }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <Bottom />
        </>
      </BlurWrapper>

      <SideBar
        isVisible={isVisible}
        closeSideBar={(data?: Record<string, boolean>) => {
          if (data) {
            setInterestsDataObj(data)
          }
          setIsVisible(false)
        }}
        selectedList={interestsDataObj}
        setSearchArgs={setSearchArgs}
        searchArgs={searchArgs}
        whatsToFocus={whatsToFocus}
      />
    </BackgroundWrapper>
  )
}

export default Explore
